import React, { useEffect, useMemo, useState } from "react";
import { taskApi } from "../../backend/client";
import {
  TaskDialog,
  TaskDialogActions,
  TaskTextArea,
  TaskTextInput,
} from "./taskFormUi";

const initial = {
  provider: "",
  address: "",
  startDate: "",
  endDate: "",
  rentAmount: "",
  bondAmount: "",
  notes: "",
  link: "",
};

const allEmpty = (obj) =>
  Object.values(obj).every((value) => String(value || "").trim() === "");

const LeaseLinkForm = ({ taskId, onClose }) => {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const canSave = useMemo(() => !saving, [saving]);

  useEffect(() => {
    const load = async () => {
      if (!taskId) return;
      const { data, error } = await taskApi.get(taskId);

      if (error) {
        console.error("Load lease_info error:", error.message);
        return;
      }

      const info = (data?.lease_info || {}) ?? {};
      setForm({
        provider: info.provider || "",
        address: info.address || "",
        startDate: info.startDate || "",
        endDate: info.endDate || "",
        rentAmount: String(info.rentAmount || ""),
        bondAmount: String(info.bondAmount || ""),
        notes: info.notes || "",
        link: info.link || "",
      });
    };

    load();
  }, [taskId]);

  const update = (key) => (event) =>
    setForm((previous) => ({ ...previous, [key]: event.target.value }));

  const handleSave = async () => {
    if (!taskId || saving) return;
    setSaving(true);

    const cleaned = Object.fromEntries(
      Object.entries(form).map(([key, value]) => [
        key,
        String(value || "").trim(),
      ]),
    );

    const toNumber = (value) =>
      value !== "" && !Number.isNaN(Number(value)) ? Number(value) : "";
    const payload = {
      ...cleaned,
      rentAmount: toNumber(cleaned.rentAmount),
      bondAmount: toNumber(cleaned.bondAmount),
    };
    const nextValue = allEmpty(payload) ? null : payload;

    const { error } = await taskApi.update(taskId, { lease_info: nextValue });

    setSaving(false);
    if (error) {
      console.error("Save lease_info error:", error.message);
      return;
    }

    onClose?.();
  };

  return (
    <TaskDialog
      title="Lease Details"
      description="Lease provider, dates, amounts, notes, and document link."
      onClose={onClose}
      className="max-w-2xl"
    >
      <div className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <TaskTextInput
            id="lease-provider"
            label="Provider / Agent"
            value={form.provider}
            onChange={update("provider")}
            placeholder="Ray White, LJ Hooker"
          />
          <TaskTextInput
            id="lease-address"
            label="Property Address"
            value={form.address}
            onChange={update("address")}
            placeholder="Street, suburb, state"
          />
          <TaskTextInput
            id="lease-start-date"
            label="Start Date"
            type="date"
            value={form.startDate}
            onChange={update("startDate")}
          />
          <TaskTextInput
            id="lease-end-date"
            label="End Date"
            type="date"
            value={form.endDate}
            onChange={update("endDate")}
          />
          <TaskTextInput
            id="lease-rent"
            label="Rent Amount"
            type="number"
            inputMode="decimal"
            value={form.rentAmount}
            onChange={update("rentAmount")}
            placeholder="AUD"
          />
          <TaskTextInput
            id="lease-bond"
            label="Bond Amount"
            type="number"
            inputMode="decimal"
            value={form.bondAmount}
            onChange={update("bondAmount")}
            placeholder="AUD"
          />
          <TaskTextArea
            id="lease-notes"
            label="Notes"
            value={form.notes}
            onChange={update("notes")}
            rows={4}
            placeholder="Key clauses, renewal info, special terms..."
            className="sm:col-span-2"
          />
          <TaskTextInput
            id="lease-link"
            label="OneDrive Link"
            type="url"
            value={form.link}
            onChange={update("link")}
            placeholder="Paste lease document link"
            className="sm:col-span-2"
          />
        </div>

        <TaskDialogActions
          onCancel={onClose}
          onSubmit={handleSave}
          disabled={!canSave}
          submitLabel={saving ? "Saving..." : "Save"}
        />
      </div>
    </TaskDialog>
  );
};

export default LeaseLinkForm;
